import { useEffect, useRef, useMemo, useCallback } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Card, CardContent, Loading, Avatar, BottomSheet } from '@/components/ui';
import { MessageBubble, DateSeparator } from '@/components/messages/MessageBubble';
import { MessageInput } from '@/components/messages/MessageInput';
import { useMessageStore, Message } from '@/stores/messageStore';
import { useAuthStore } from '@/stores/authStore';

// Group messages by date
function groupMessagesByDate(messages: Message[]): { date: Date; messages: Message[] }[] {
  const groups: { date: Date; messages: Message[] }[] = [];
  let currentDate: string | null = null;
  let currentGroup: Message[] = [];

  messages.forEach((message) => {
    const messageDate = new Date(message.sentAt);
    const dateKey = messageDate.toDateString();

    if (dateKey !== currentDate) {
      if (currentGroup.length > 0) {
        groups.push({
          date: new Date(currentDate!),
          messages: currentGroup,
        });
      }
      currentDate = dateKey;
      currentGroup = [message];
    } else {
      currentGroup.push(message);
    }
  });

  if (currentGroup.length > 0) {
    groups.push({
      date: new Date(currentDate!),
      messages: currentGroup,
    });
  }

  return groups;
}

interface ChatBottomSheetProps {
  conversationId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenProfile?: (userId: string) => void;
}

export function ChatBottomSheet({ conversationId, isOpen, onClose, onOpenProfile }: ChatBottomSheetProps) {
  const user = useAuthStore((state) => state.user);
  const messages = useMessageStore((state) => state.messages);
  const currentConversation = useMessageStore((state) => state.currentConversation);
  const isLoading = useMessageStore((state) => state.isLoading);
  const isSending = useMessageStore((state) => state.isSending);
  const error = useMessageStore((state) => state.error);
  const subscribeToMessages = useMessageStore((state) => state.subscribeToMessages);
  const unsubscribeFromMessages = useMessageStore((state) => state.unsubscribeFromMessages);
  const getConversation = useMessageStore((state) => state.getConversation);
  const setCurrentConversation = useMessageStore((state) => state.setCurrentConversation);
  const sendMessage = useMessageStore((state) => state.sendMessage);
  const sendImageMessage = useMessageStore((state) => state.sendImageMessage);
  const markAsRead = useMessageStore((state) => state.markAsRead);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Fetch conversation details
  useEffect(() => {
    const fetchConversation = async () => {
      if (conversationId && user?.uid && isOpen) {
        const conversation = await getConversation(conversationId, user.uid);
        if (conversation) {
          setCurrentConversation(conversation);
        }
      }
    };

    if (isOpen) {
      fetchConversation();
    }

    return () => {
      if (!isOpen) {
        setCurrentConversation(null);
      }
    };
  }, [conversationId, user?.uid, isOpen, getConversation, setCurrentConversation]);

  // Subscribe to messages
  useEffect(() => {
    if (conversationId && isOpen) {
      subscribeToMessages(conversationId);
    }

    return () => {
      unsubscribeFromMessages();
    };
  }, [conversationId, isOpen, subscribeToMessages, unsubscribeFromMessages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && isOpen) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Mark unread messages as read
  useEffect(() => {
    if (!conversationId || !user?.uid || messages.length === 0 || !isOpen) return;

    const unreadMessages = messages.filter(
      (msg) => msg.senderId !== user.uid && !msg.readAt
    );

    if (unreadMessages.length > 0) {
      markAsRead(
        conversationId,
        unreadMessages.map((msg) => msg.id),
        user.uid
      );
    }
  }, [conversationId, user?.uid, messages, markAsRead, isOpen]);

  // Group messages by date
  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages]);

  // Handle send message
  const handleSendMessage = useCallback(async (content: string) => {
    if (!conversationId || !user?.uid) return;
    await sendMessage(conversationId, user.uid, content);
  }, [conversationId, user?.uid, sendMessage]);

  // Handle send image
  const handleSendImage = useCallback(async (file: File) => {
    if (!conversationId || !user?.uid) return;
    await sendImageMessage(conversationId, user.uid, file);
  }, [conversationId, user?.uid, sendImageMessage]);

  // Handle close
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOpenProfile = useCallback(() => {
    const participantId = currentConversation?.participant.id;
    if (!participantId || !onOpenProfile) return;
    onOpenProfile(participantId);
  }, [currentConversation?.participant.id, onOpenProfile]);

  // Content based on state
  const renderContent = () => {
    // Loading state (no conversation yet)
    if (!currentConversation && isLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="flex-1 flex items-center justify-center p-4">
          <Card>
            <CardContent className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600">{error}</p>
              <button
                onClick={handleClose}
                className="text-copper hover:underline mt-4 inline-block"
              >
                メッセージ一覧に戻る
              </button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // No conversation found
    if (!currentConversation) {
      return (
        <div className="flex-1 flex items-center justify-center p-4">
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-neutral-600">会話が見つかりませんでした</p>
              <button
                onClick={handleClose}
                className="text-copper hover:underline mt-4 inline-block"
              >
                メッセージ一覧に戻る
              </button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <>
        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto bg-[#F7F4EF] px-4 py-4"
        >
          {/* Loading messages */}
          {isLoading && messages.length === 0 && (
            <div className="flex justify-center py-8">
              <Loading />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="mb-2 text-[15px] font-medium text-[#AAA]">
                まだメッセージがありません
              </p>
              <p className="text-[13px] text-[#C0BDB8]">
                最初のメッセージを送ってみましょう
              </p>
            </div>
          )}

          {/* Messages */}
          {messageGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              <DateSeparator date={group.date} />
              {group.messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.senderId === user?.uid}
                />
              ))}
            </div>
          ))}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <MessageInput
          onSendMessage={handleSendMessage}
          onSendImage={handleSendImage}
          isSending={isSending}
        />
      </>
    );
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      height="fixed"
      showHandle={true}
      showCloseButton={false}
      mobileFullScreen={true}
    >
      <div className="flex h-full flex-col">
        {/* Custom Header */}
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#EDEBE6] bg-white px-4 py-3">
          <button
            onClick={handleClose}
            className="p-2 -m-2 text-[#B87333] transition hover:text-[#9A5E29]"
          >
            <X className="w-5 h-5" />
          </button>
          {currentConversation && (
            <>
              {onOpenProfile ? (
                <>
                  <button
                    type="button"
                    onClick={handleOpenProfile}
                    className="flex-shrink-0"
                  >
                    <Avatar
                      src={currentConversation.participant.iconUrl}
                      alt={currentConversation.participant.displayName}
                      size="sm"
                      className="rounded-full bg-[#D4C5A9]"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenProfile}
                    className="flex-1 min-w-0 text-left"
                  >
                    <h1 className="truncate text-[15px] font-semibold text-[#1A1A1A]">
                      {currentConversation.participant.displayName}
                    </h1>
                  </button>
                </>
              ) : (
                <>
                  <Avatar
                    src={currentConversation.participant.iconUrl}
                    alt={currentConversation.participant.displayName}
                    size="sm"
                    className="rounded-full bg-[#D4C5A9]"
                  />
                  <div className="flex-1 min-w-0">
                    <h1 className="truncate text-[15px] font-semibold text-[#1A1A1A]">
                      {currentConversation.participant.displayName}
                    </h1>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {renderContent()}
      </div>
    </BottomSheet>
  );
}
